import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { composeIsoAndIsDeletedWhereCondition } from "../../components/Filter/ApplyFilters";
import { getVisibleFilters } from "../../utils/helper";
import useLayersChange from "../useLayersChange";

const useFiltersFetch = (config) => {
  const [fieldsValueCountMap, setFieldsValueCountMap] = useState({});
  const [fieldsRelationsMap, setFieldsRelationsMap] = useState({});
  const [fieldsLayers, setFieldsLayers] = useState({});

  const { layerCount } = useLayersChange(config);
  const filters = useSelector((state) => state.filters);
  const activeModule = useSelector((state) => state.activeModule);
  const cacheRef = useRef(new Map());

  const activeFilters = useMemo(() => {
    return Object.values(getVisibleFilters(config, activeModule, filters));
  }, [config, layerCount, filters, activeModule]);

  const configFilteredFields = useMemo(() => {
    const result = {};
    if (!config) return result;

    if (Array.isArray(config.filterFields)) {
      config.filterFields.forEach((f) => {
        result[f.name] = f;
      });
    }

    return result;
  }, [config]);

  useEffect(() => {
    // const filtersToFetch = activeFilters.filter((filter) => {
    //   const filterField = configFilteredFields[filter.field.name];
    //   return filterField?.showCount;
    // });

    const abortController = new AbortController();

    const cacheKey = JSON.stringify(
      activeFilters.map((f) => ({
        field: f.field?.name,
        allLayers: f.allLayers?.map((l) => l.id), // or another unique identifier
      }))
    );

    if (cacheRef.current.has(cacheKey)) {
      const { fieldsMap, fieldRelationFields, fieldsLayers } =
        cacheRef.current.get(cacheKey);
      setFieldsRelationsMap(fieldRelationFields);
      setFieldsValueCountMap(fieldsMap);
      setFieldsLayers(fieldsLayers);
      return;
    }

    const getFieldsMap = async () => {
      const filtersToFetch = activeFilters;

      const fieldsMap = {};
      const fieldRelationFields = {};
      const fieldsLayers = {};
      await Promise.allSettled(
        filtersToFetch.map(async (filter) => {
          const { field, l, allLayers } = filter;
          await Promise.allSettled(
            allLayers.map(async (layer) => {
              const relationFieldNames = []; //["wastetypeen", "disposalmethod"];
              // fieldsMap[layerId] = {
              //   [field.name]: new Map(),
              // };

              if (!fieldsMap[field.name]) {
                fieldsMap[field.name] = new Map();
              }
              fieldsLayers[field.name] = allLayers;

              if (field.domain && field.domain.codedValues) {
                const query = layer.createQuery();
                query.where = composeIsoAndIsDeletedWhereCondition(
                  layer,
                  config
                );

                query.outStatistics = [
                  {
                    statisticType: "count",
                    onStatisticField: field.name,
                    outStatisticFieldName: "value_count",
                  },
                ];
                query.groupByFieldsForStatistics = [
                  field.name,
                  ...relationFieldNames,
                ];
                const relationFields = new Map();
                try {
                  const result = await layer.queryFeatures(query, {
                    signal: abortController.signal,
                  });

                  result.features.forEach((feature) => {
                    const value = feature.attributes[field.name];

                    const count = feature.attributes.value_count;
                    if (!relationFields.has(value)) {
                      relationFields.set(value, new Map());
                    }

                    relationFieldNames.forEach((relField) => {
                      const relValue = feature.attributes[relField];
                      const fieldRelMap = relationFields.get(value);

                      if (!fieldRelMap.has(relField)) {
                        fieldRelMap.set(relField, new Set());
                      }
                      fieldRelMap.get(relField).add(relValue);
                    });
                    const existingCount = fieldsMap[field.name].get(value) || 0;
                    fieldsMap[field.name].set(value, existingCount + count);
                  });
                } catch (err) {
                  // console.log(err);
                }

                // 2. Separate count for nulls (if nullable)
                if (field.nullable) {
                  try {
                    const nullQuery = layer.createQuery();
                    nullQuery.where = `${field.name} IS NULL`;
                    nullQuery.outFields = relationFieldNames; // fetch relation fields
                    nullQuery.returnGeometry = false;

                    const result = await layer.queryFeatures(nullQuery, {
                      signal: abortController.signal,
                    });

                    // Aggregate count and relation fields for nulls
                    let count = 0;
                    if (result.features.length > 0) {
                      count = result.features.length;
                      const nullRelObject = new Map();
                      result.features.forEach((feature) => {
                        relationFieldNames.forEach((relField) => {
                          if (!nullRelObject.has(relField)) {
                            nullRelObject.set(relField, new Set());
                          }
                          const relValue = feature.attributes[relField];
                          nullRelObject.get(relField).add(relValue);
                        });
                      });
                      relationFields.set(null, nullRelObject);
                    }

                    fieldsMap[field.name].set(null, count);
                  } catch (err) {
                    // console.log(err);
                  }
                }

                fieldRelationFields[field.name] = relationFields;
              }
            })
          );
        })
      );

      return { fieldsMap, fieldRelationFields, fieldsLayers };
    };

    let isMounted = true;
    getFieldsMap().then(({ fieldsMap, fieldRelationFields, fieldsLayers }) => {
      if (!isMounted) return;
      cacheRef.current.set(cacheKey, {
        fieldsMap,
        fieldRelationFields,
        fieldsLayers,
      });

      setFieldsRelationsMap(fieldRelationFields);
      setFieldsValueCountMap(fieldsMap);
      setFieldsLayers(fieldsLayers);
    });

    return () => {
      isMounted = false;
      abortController.abort();
    };
  }, [activeFilters, configFilteredFields]);

  return { fieldsValueCountMap, fieldsRelationsMap, fieldsLayers };
};

export default useFiltersFetch;
